import { ReactNode, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { track } from "@/components/layernav/navConfig";

export const CARD = "bg-[#0b0c10] border border-white/[0.08] rounded-xl";

/* Hero-section cards match the pure-black site canvas (founder directive) */
export const CARD_HERO = "bg-[#000000] border border-[#1c212e] rounded-xl";

export const HOME_CONTAINER = "max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10";

export const homeTrack = (module: string, target?: string) =>
  track("home_click", { module, target });

export const Reveal = ({ children, className = "", delayMs = 0 }: {
  children: ReactNode; className?: string; delayMs?: number;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(true);
      return;
    }
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); io.disconnect(); }
    }, { rootMargin: "0px 0px -40px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return (
    <div ref={ref} className={className}
      style={{
        opacity: shown ? 1 : 0,
        transform: shown ? "none" : "translateY(8px)",
        transition: `opacity 220ms ease ${delayMs}ms, transform 220ms ease ${delayMs}ms`,
      }}>
      {children}
    </div>
  );
};

/* Section header — Figma 247:2 type system (founder consistency order 2026-08-16):
   Inter kicker #9c7cff 11/14 ls3 · title Inter 700 44/52 #f2f5fc · sub 16/22 #969cab
   · cyan CTA #0dd6eb 14/22 600. Single source of truth for every section header. */
export const SectionHead = ({ label, title, sub, cta, to, testid, module, ctaStyle = "link", sparkle = false }: {
  label?: string; title: string; sub?: string; cta?: string; to?: string; testid?: string; module?: string;
  ctaStyle?: "link" | "button"; sparkle?: boolean;
}) => (
  <div className="flex flex-wrap items-start justify-between gap-4 mb-[15px]"
    style={{ fontFamily: "Inter, 'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
    <div className="min-w-0">
      {label && (
        <p className="text-[#9c7cff] font-semibold text-[11px] leading-[14px] tracking-[3px] uppercase">{label}</p>
      )}
      <h2 className={`text-[#f2f5fc] font-bold text-[32px] leading-[38px] sm:text-[44px] sm:leading-[52px] tracking-tight ${label ? "mt-[15px]" : ""}`}>
        {title}
      </h2>
      {sub && <p className="text-[#969cab] text-[15px] sm:text-[16px] leading-[22px] mt-1 max-w-[762px]">{sub}</p>}
    </div>
    {cta && to && (
      <Link to={to} data-testid={testid} onClick={() => homeTrack(module || title, to)}
        className={ctaStyle === "button"
          ? `${sparkle ? "sparkle-btn " : ""}group inline-flex items-center gap-1.5 rounded-lg bg-[#7C3AED] hover:bg-[#6b2fd6] text-white text-[13px] font-bold px-4 py-2 transition-colors sm:mt-[38px]`
          : "group inline-flex items-center gap-1.5 text-[#0dd6eb] hover:text-white font-semibold text-[14px] leading-[22px] transition-colors sm:mt-[38px]"}>
        {cta}
        <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
      </Link>
    )}
  </div>
);

export const CardCTA = ({ to, children, testid, module }: {
  to: string; children: ReactNode; testid?: string; module?: string;
}) => (
  <Link to={to} data-testid={testid} onClick={() => homeTrack(module || "card", to)}
    className="group/cta inline-flex items-center gap-1.5 text-[12px] font-bold text-[#0dd6eb] hover:text-white transition-colors mt-auto pt-3">
    {children}
    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/cta:translate-x-1" />
  </Link>
);

const STATUS_META: Record<string, { label: string; color: string }> = {
  proposed: { label: "Proposed", color: "#00E5FF" },
  in_progress: { label: "In Review", color: "#F59E0B" },
  enacted: { label: "Passed", color: "#84CC16" },
  enforced: { label: "Effective", color: "#22C55E" },
  rejected: { label: "Rejected", color: "#EF4444" },
};

export const RegStatus = ({ status, pill }: { status: string; pill?: boolean }) => {
  const m = STATUS_META[status] || { label: status.replace("_", " "), color: "#A3A3A3" };
  if (pill) {
    return (
      <span className="inline-flex items-center font-mono text-[8.5px] font-bold uppercase tracking-wider border rounded px-1.5 py-0.5"
        style={{ color: m.color, borderColor: `${m.color}66`, background: `${m.color}14` }}>
        {m.label}
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-wider"
      style={{ color: m.color }}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: m.color }} />
      {m.label}
    </span>
  );
};

export const JURISDICTION_SHORT: Record<string, string> = {
  "Global": "GLOBAL",
  "United States": "US",
  "European Union": "EU",
  "United Kingdom": "UK",
  "Asia-Pacific": "APAC",
};
